import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftCalendarView = ({ 
  shifts = [], 
  selectedDate = new Date(), 
  onDateChange, 
  onShiftClick, 
  onShiftDrop,
  onShiftDelete,
  viewMode = 'week' // 'week' or 'month'
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [draggedShift, setDraggedShift] = useState(null);

  // Update currentDate when selectedDate prop changes
  useEffect(() => {
    setCurrentDate(selectedDate);
  }, [selectedDate]);

  const departments = [
    { id: 'production', name: 'Produktion', color: '' },
    { id: 'warehouse', name: 'Lager', color: 'bg-green-500' },
    { id: 'quality', name: 'Qualitätssicherung', color: 'bg-purple-500' },
    { id: 'maintenance', name: 'Wartung', color: 'bg-orange-500' },
    { id: 'admin', name: 'Verwaltung', color: 'bg-blue-500' }
  ];

  // Use only the shifts passed from parent component (real data)
  const allShifts = shifts || [];

  const getDepartmentColor = (departmentId) => {
    const dept = departments?.find(d => d?.id === departmentId);
    return dept ? dept?.color : 'bg-blue-500';
  };

  const getWeekDates = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek?.getDay();
    const diff = startOfWeek?.getDate() - day;
    startOfWeek?.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const weekDate = new Date(startOfWeek);
      weekDate?.setDate(startOfWeek?.getDate() + i);
      week?.push(weekDate);
    }
    return week;
  };

  const getMonthDates = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    const endDate = new Date(lastDay);

    // Adjust to start from Sunday
    startDate?.setDate(startDate?.getDate() - startDate?.getDay());
    endDate?.setDate(endDate?.getDate() + (6 - endDate?.getDay()));

    const dates = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      dates?.push(new Date(current));
      current?.setDate(current?.getDate() + 1);
    }
    
    return dates;
  };

  const formatDate = (date) => {
    return date?.toISOString()?.split('T')?.[0];
  };

  const getShiftsForDate = (date) => {
    const dateStr = formatDate(date);
    return allShifts?.filter(shift => shift?.date === dateStr);
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate?.setDate(newDate?.getDate() - 7);
    } else {
      newDate?.setMonth(newDate?.getMonth() - 1);
    }
    setCurrentDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate?.setDate(newDate?.getDate() + 7);
    } else {
      newDate?.setMonth(newDate?.getMonth() + 1);
    }
    setCurrentDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    if (onDateChange) onDateChange(today);
  };

  const handleShiftDragStart = (e, shift) => {
    setDraggedShift(shift);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleShiftDragEnd = () => {
    setDraggedShift(null);
  };

  const handleDateDrop = (e, targetDate) => {
    e?.preventDefault();
    if (draggedShift && onShiftDrop) {
      onShiftDrop(draggedShift, formatDate(targetDate));
    }
    setDraggedShift(null);
  };

  const handleDateDragOver = (e) => {
    e?.preventDefault();
  };

  const handleShiftDelete = (e, shift) => {
    e?.stopPropagation();
    if (onShiftDelete) {
      onShiftDelete(shift);
    }
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);
    const timeSlots = [];
    
    // Generate time slots from 6 AM to 11 PM
    for (let hour = 6; hour <= 23; hour++) {
      timeSlots?.push(`${hour?.toString()?.padStart(2, '0')}:00`);
    }

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Week Header */}
        <div className="grid grid-cols-8 border-b border-border">
          <div className="p-4 bg-muted font-medium text-text-secondary">Zeit</div>
          {weekDates?.map((date, index) => (
            <div key={index} className="p-4 text-center border-l border-border">
              <div className="font-medium text-text-primary">
                {date?.toLocaleDateString('de-DE', { weekday: 'short' })}
              </div>
              <div className="text-sm text-text-secondary">
                {date?.getDate()}
              </div>
            </div>
          ))}
        </div>
        {/* Time Grid */}
        <div className="max-h-[600px] overflow-y-auto">
          {timeSlots?.map((time, timeIndex) => (
            <div key={timeIndex} className="grid grid-cols-8 border-b border-border min-h-[80px]">
              <div className="p-2 bg-muted text-sm text-text-secondary border-r border-border">
                {time}
              </div>
              {weekDates?.map((date, dateIndex) => {
                const dayShifts = getShiftsForDate(date);
                const timeShifts = dayShifts?.filter(shift => {
                  const shiftStart = parseInt(shift?.startTime?.split(':')?.[0]);
                  const currentHour = parseInt(time?.split(':')?.[0]);
                  return shiftStart === currentHour;
                });

                return (
                  <div
                    key={dateIndex}
                    className="border-l border-border p-2 relative"
                    onDrop={(e) => handleDateDrop(e, date)}
                    onDragOver={handleDateDragOver}
                  >
                    {timeShifts?.map((shift) => (
                      <div
                        key={shift?.id}
                        draggable
                        onDragStart={(e) => handleShiftDragStart(e, shift)}
                        onDragEnd={handleShiftDragEnd}
                        onClick={() => onShiftClick && onShiftClick(shift)}
                        className={`${getDepartmentColor(shift?.department)} text-white text-xs p-2 rounded mb-1 cursor-pointer hover:opacity-80 transition-opacity relative group`}
                      >
                        <div className="font-medium truncate">{shift?.employeeName}</div>
                        <div className="truncate">{shift?.startTime}-{shift?.endTime}</div>
                        <div className="truncate opacity-75">{shift?.location}</div>
                        {onShiftDelete && (
                          <button
                            onClick={(e) => handleShiftDelete(e, shift)}
                            className="absolute top-0 right-0 w-4 h-4 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Schicht löschen"
                          >
                            <Icon name="X" size={10} className="text-white" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(currentDate);
    const weeks = [];
    
    for (let i = 0; i < monthDates?.length; i += 7) {
      weeks?.push(monthDates?.slice(i, i + 7));
    }

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Month Header */}
        <div className="grid grid-cols-7 border-b border-border">
          {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']?.map((day) => (
            <div key={day} className="p-4 text-center font-medium text-text-secondary bg-muted">
              {day}
            </div>
          ))}
        </div>
        {/* Month Grid */}
        {weeks?.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 border-b border-border last:border-b-0">
            {week?.map((date, dateIndex) => {
              const dayShifts = getShiftsForDate(date);
              const isCurrentMonth = date?.getMonth() === currentDate?.getMonth();
              const isToday = formatDate(date) === formatDate(new Date());

              return (
                <div
                  key={dateIndex}
                  className={`min-h-[150px] p-3 border-l border-border first:border-l-0 ${
                    !isCurrentMonth ? 'bg-muted/30' : ''
                  } ${isToday ? 'bg-primary/5' : ''}`}
                  onDrop={(e) => handleDateDrop(e, date)}
                  onDragOver={handleDateDragOver}
                >
                  <div className={`text-sm font-medium mb-2 ${
                    !isCurrentMonth ? 'text-text-secondary' : 'text-text-primary'
                  } ${isToday ? 'text-primary' : ''}`}>
                    {date?.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayShifts?.slice(0, 3)?.map((shift) => (
                      <div
                        key={shift?.id}
                        draggable
                        onDragStart={(e) => handleShiftDragStart(e, shift)}
                        onDragEnd={handleShiftDragEnd}
                        onClick={() => onShiftClick && onShiftClick(shift)}
                        className={`${getDepartmentColor(shift?.department)} text-white text-xs p-2 rounded cursor-pointer hover:opacity-80 transition-opacity relative group`}
                      >
                        <div className="font-medium truncate">{shift?.employeeName}</div>
                        <div className="truncate">{shift?.startTime}-{shift?.endTime}</div>
                        {onShiftDelete && (
                          <button
                            onClick={(e) => handleShiftDelete(e, shift)}
                            className="absolute top-0 right-0 w-3 h-3 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Schicht löschen"
                          >
                            <Icon name="X" size={8} className="text-white" />
                          </button>
                        )}
                      </div>
                    ))}
                    {dayShifts?.length > 3 && (
                      <div className="text-xs text-text-secondary bg-muted rounded p-1">
                        +{dayShifts?.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const getDateRangeText = () => {
    if (viewMode === 'week') {
      const weekDates = getWeekDates(currentDate);
      const start = weekDates?.[0];
      const end = weekDates?.[6];
      
      if (start?.getMonth() === end?.getMonth()) {
        return `${start?.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end?.getDate()}, ${start?.getFullYear()}`;
      } else {
        return `${start?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${start?.getFullYear()}`;
      }
    } else {
      return currentDate?.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Calendar Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-text-primary">
            {getDateRangeText()}
          </h2>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevious}
              iconName="ChevronLeft"
              className="touch-feedback"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              className="touch-feedback"
            >
              Heute
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              iconName="ChevronRight"
              className="touch-feedback"
            />
          </div>
        </div>

        {/* Department Legend */}
        <div className="flex items-center space-x-4">
          {/* <span className="text-sm text-text-secondary">Departments:</span>
          <div className="flex items-center space-x-3">
            {departments?.map((dept) => (
              <div key={dept?.id} className="flex items-center space-x-1">
                <div className={`w-3 h-3 rounded ${dept?.color}`}></div>
                <span className="text-xs text-text-secondary">{dept?.name}</span>
              </div>
            ))}
          </div> */}
        </div>
      </div>
      {/* Calendar View */}
      {viewMode === 'week' ? renderWeekView() : renderMonthView()}
      {/* Drag Instructions */}
      {draggedShift && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <Icon name="Move" size={16} />
            <span className="text-sm">Zum Verschieben ziehen</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftCalendarView;